import { useState } from "react";
import {
  useAddAdminUserCredentialsMutation,
  useSetAdminUserPasswordMutation,
} from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { Field, FieldLabel } from "../field";
import { Input } from "../input";

const MIN_PASSWORD_LENGTH = 10;

export default function AdminPasswordDialog({
  mode,
  userID,
  onOpenChange,
  onError,
}: {
  mode: "reset" | "create" | null;
  userID: string;
  onOpenChange: (open: boolean) => void;
  onError: (message: string | null) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [setPasswordMutation, { isLoading: isResetting }] =
    useSetAdminUserPasswordMutation();
  const [addCredentials, { isLoading: isCreating }] =
    useAddAdminUserCredentialsMutation();

  const reset = () => {
    setUsername("");
    setPassword("");
    setConfirm("");
    setLocalError(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) reset();
    onOpenChange(open);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setLocalError(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`,
      );
      return;
    }
    if (password !== confirm) {
      setLocalError("Passwords do not match.");
      return;
    }

    onError(null);
    try {
      if (mode === "reset") {
        await setPasswordMutation({ userID, password }).unwrap();
      } else {
        await addCredentials({ userID, username, password }).unwrap();
      }
      handleOpenChange(false);
    } catch (e) {
      setLocalError(
        getApiErrorDetail(e, "Could not save that password. Please try again."),
      );
    }
  };

  const isLoading = isResetting || isCreating;

  return (
    <Dialog open={mode !== null} onOpenChange={handleOpenChange}>
      <DialogContent>
        {/* The form must live inside DialogContent, not wrap it: DialogContent
            portals its children to document.body, so a <form> wrapping it
            would have no real DOM descendants and its submit button would
            never fire an onSubmit. */}
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {mode === "reset" ? "Reset password" : "Add password login"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">
              This signs the user out of every password session.
            </p>
            {mode === "create" && (
              <Field>
                <FieldLabel htmlFor="adminNewUsername">Username</FieldLabel>
                <Input
                  id="adminNewUsername"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                />
              </Field>
            )}
            <Field>
              <FieldLabel htmlFor="adminNewPassword">New password</FieldLabel>
              <Input
                id="adminNewPassword"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="adminConfirmPassword">
                Confirm password
              </FieldLabel>
              <Input
                id="adminConfirmPassword"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            {localError && (
              <p role="alert" className="text-sm text-destructive">
                {localError}
              </p>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isLoading}>
              {mode === "reset" ? "Reset password" : "Add password login"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
